// Admin Badges — configure points-milestone badges. Badges auto-award when a learner's
// Course + Quiz points (Arcade excluded) cross the threshold.
import React, { useCallback, useEffect, useState } from 'react';
import { api, mediaUrl } from '@/lib/api';
import { toast } from 'sonner';
import { Award, Loader2, Plus, Trash2, Trophy, Image as ImageIcon, ListChecks, X, Check } from 'lucide-react';

const COLORS = ['gold', 'emerald', 'blue', 'violet', 'rose', 'slate'];
const colorClass = (c) => ({
    gold: 'from-amber-500 to-orange-500',
    emerald: 'from-emerald-500 to-teal-600',
    blue: 'from-blue-500 to-indigo-600',
    violet: 'from-violet-500 to-fuchsia-500',
    rose: 'from-rose-500 to-pink-500',
    slate: 'from-slate-600 to-slate-800',
}[c] || 'from-amber-500 to-orange-500');

const inputCls = 'w-full px-3 py-2 rounded-lg ring-1 ring-slate-300 focus:ring-2 focus:ring-amber-400 focus:outline-none text-sm bg-white';

export default function AdminBadges() {
    const [badges, setBadges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [color, setColor] = useState('gold');
    const [quizFor, setQuizFor] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/admin/badges');
            setBadges(r.data || []);
        } catch { toast.error('Failed to load badges'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const onCreate = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        const fd = new FormData(form);
        if (!String(fd.get('name') || '').trim()) { toast.error('Badge name is required'); return; }
        if (!String(fd.get('points_required') || '').trim()) { toast.error('Points threshold is required'); return; }
        fd.set('color', color);
        setCreating(true);
        try {
            await api.post('/admin/badges', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success('Badge created');
            form.reset(); setColor('gold');
            load();
        } catch (err) {
            toast.error(`Failed: ${err?.response?.data?.detail || err.message}`);
        } finally { setCreating(false); }
    };

    const onDelete = async (id) => {
        if (!window.confirm('Delete this badge? Learners will no longer see it.')) return;
        try { await api.delete(`/admin/badges/${id}`); toast.success('Deleted'); load(); }
        catch { toast.error('Delete failed'); }
    };

    const onToggle = async (b) => {
        try { await api.patch(`/admin/badges/${b.id}`, { is_active: !b.is_active }); load(); }
        catch { toast.error('Update failed'); }
    };

    const milestones = badges.filter((b) => b.type === 'milestone');
    const legacy = badges.filter((b) => b.type !== 'milestone');

    return (
        <div className="space-y-6" data-testid="admin-badges-page">
            <div>
                <h1 className="font-display font-extrabold text-2xl text-slate-900 flex items-center gap-2">
                    <Award className="h-6 w-6 text-amber-500" /> Badges
                </h1>
                <p className="text-sm text-slate-500 mt-1">
                    Badges are earned from <b>Course &amp; Quiz points only</b> (Arcade points are not counted).
                    Set a points threshold — learners auto-earn the badge once they reach it.
                    Add a <b>quiz</b> to a badge and the milestone instead <b>unlocks the quiz</b>; learners earn the badge by passing it.
                </p>
            </div>

            {/* Configurator */}
            <form onSubmit={onCreate} className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm p-5 space-y-3" data-testid="badge-create-form">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-amber-600">
                    <Plus className="w-4 h-4" /> Create a badge
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input name="name" placeholder="Badge name (e.g. Rising Star)" className={inputCls} data-testid="badge-name" />
                    <input name="points_required" type="number" min="0" placeholder="Points required (e.g. 1000)" className={inputCls} data-testid="badge-points" />
                    <input name="pass_percent" type="number" min="0" max="100" defaultValue={70} placeholder="Quiz pass % (default 70)" className={inputCls} data-testid="badge-pass-percent" />
                </div>
                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                    <ImageIcon className="w-4 h-4 text-slate-400" />
                    <input type="file" name="image" accept="image/png,image/jpeg,image/webp,image/gif" className="text-xs" data-testid="badge-image" />
                    <span className="text-xs text-slate-400">(optional badge image)</span>
                </label>
                <input name="description" placeholder="Description (shown to the learner)" className={inputCls} data-testid="badge-description" />
                <input name="reward_sku" placeholder="Zeapl reward SKU (optional, e.g. BADGE-001) — credited to loyalty when awarded" className={inputCls} data-testid="badge-reward-sku" />
                <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xs font-bold text-slate-500">Accent color:</span>
                    {COLORS.map((c) => (
                        <button type="button" key={c} onClick={() => setColor(c)}
                            className={`w-7 h-7 rounded-full bg-gradient-to-br ${colorClass(c)} ${color === c ? 'ring-2 ring-offset-2 ring-slate-800' : ''}`}
                            data-testid={`badge-color-${c}`} aria-label={c} />
                    ))}
                    <button type="submit" disabled={creating}
                        className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-sm disabled:opacity-50"
                        data-testid="badge-submit">
                        {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        {creating ? 'Creating…' : 'Create Badge'}
                    </button>
                </div>
            </form>

            {/* Milestone badges */}
            <section>
                <h2 className="font-display font-bold text-lg text-slate-900 mb-3 flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-amber-500" /> Milestone badges ({milestones.length})
                </h2>
                {loading && <Loader2 className="w-5 h-5 animate-spin text-slate-400" />}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {milestones.map((b) => (
                        <div key={b.id} className={`bg-white border border-slate-200 rounded-2xl p-4 ${b.is_active ? '' : 'opacity-60'}`} data-testid={`badge-${b.id}`}>
                            <div className="flex items-start gap-3">
                                {b.image_url
                                    ? <img src={mediaUrl(b.image_url)} alt={b.name} className="h-12 w-12 rounded-xl object-cover" />
                                    : <div className={`h-12 w-12 rounded-xl grid place-items-center font-display font-extrabold text-white text-lg bg-gradient-to-br ${colorClass(b.color)}`}>{b.name?.[0]}</div>}
                                <div className="flex-1 min-w-0">
                                    <div className="font-display font-bold text-slate-900 truncate">{b.name}</div>
                                    <div className="text-amber-600 font-extrabold text-sm">{b.points_required} pts</div>
                                </div>
                                <button onClick={() => onDelete(b.id)} className="p-1.5 rounded-lg hover:bg-rose-50" data-testid={`badge-del-${b.id}`}>
                                    <Trash2 className="w-4 h-4 text-rose-600" />
                                </button>
                            </div>
                            {b.description && <div className="text-xs text-slate-500 mt-2 line-clamp-2">{b.description}</div>}
                            <SkuEditor badge={b} onSaved={load} />
                            <div className="mt-3 flex items-center gap-2 flex-wrap">
                                <button onClick={() => onToggle(b)} data-testid={`badge-toggle-${b.id}`}
                                    className={`text-xs font-bold px-3 py-1 rounded-full ${b.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                                    {b.is_active ? 'Active' : 'Inactive'}
                                </button>
                                <button onClick={() => setQuizFor(b)} data-testid={`badge-quiz-manage-${b.id}`}
                                    className={`text-xs font-bold px-3 py-1 rounded-full inline-flex items-center gap-1 ${b.quiz_question_count > 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                                    <ListChecks className="w-3.5 h-3.5" />
                                    {b.quiz_question_count > 0 ? `Quiz · ${b.quiz_question_count}Q · ${b.pass_percent}%` : 'Add quiz'}
                                </button>
                            </div>
                        </div>
                    ))}
                    {!loading && milestones.length === 0 && (
                        <div className="col-span-full text-center text-slate-400 text-sm py-8">No badges yet. Create your first one above.</div>
                    )}
                </div>
            </section>

            {legacy.length > 0 && (
                <section>
                    <h2 className="font-display font-bold text-base text-slate-700 mb-2">Built-in achievement badges</h2>
                    <div className="flex flex-wrap gap-2">
                        {legacy.map((b) => (
                            <span key={b.code} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 text-xs font-bold" data-testid={`legacy-badge-${b.code}`}>
                                <Award className="w-3.5 h-3.5 text-amber-500" /> {b.name}
                            </span>
                        ))}
                    </div>
                </section>
            )}
            {quizFor && (
                <BadgeQuizManager badge={quizFor} onClose={() => { setQuizFor(null); load(); }} />
            )}
        </div>
    );
}

const optInput = 'flex-1 px-3 py-1.5 rounded-lg ring-1 ring-slate-300 focus:ring-2 focus:ring-amber-400 focus:outline-none text-sm';

function SkuEditor({ badge, onSaved }) {
    const [sku, setSku] = useState(badge.reward_sku || '');
    const [saving, setSaving] = useState(false);
    const dirty = (sku || '') !== (badge.reward_sku || '');
    const save = async () => {
        setSaving(true);
        try { await api.patch(`/admin/badges/${badge.id}`, { reward_sku: sku.trim() }); toast.success('Reward SKU saved'); onSaved && onSaved(); }
        catch { toast.error('Could not save SKU'); }
        finally { setSaving(false); }
    };
    return (
        <div className="mt-2 flex items-center gap-1.5">
            <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">SKU</span>
            <input value={sku} onChange={(e) => setSku(e.target.value)} placeholder="BADGE-001"
                className="flex-1 px-2 py-1 rounded-lg ring-1 ring-slate-200 focus:ring-2 focus:ring-amber-400 focus:outline-none text-xs font-mono"
                data-testid={`badge-sku-input-${badge.id}`} />
            {dirty && (
                <button onClick={save} disabled={saving} className="text-[11px] font-bold px-2 py-1 rounded-lg bg-amber-500 text-slate-900 disabled:opacity-50" data-testid={`badge-sku-save-${badge.id}`}>
                    {saving ? '…' : 'Save'}
                </button>
            )}
        </div>
    );
}


function BadgeQuizManager({ badge, onClose }) {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [opts, setOpts] = useState(['', '', '', '']);
    const [correct, setCorrect] = useState(0);
    const [explanation, setExplanation] = useState('');
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try { const r = await api.get(`/admin/badges/${badge.id}/quiz`); setQuestions(r.data || []); }
        catch { toast.error('Failed to load questions'); }
        finally { setLoading(false); }
    }, [badge.id]);

    useEffect(() => { load(); }, [load]);

    const addQuestion = async () => {
        const cleanOpts = opts.map((o) => o.trim()).filter(Boolean);
        if (!q.trim()) { toast.error('Question text is required'); return; }
        if (cleanOpts.length < 2) { toast.error('Add at least 2 options'); return; }
        if (correct >= cleanOpts.length) { toast.error('Pick a valid correct option'); return; }
        setSaving(true);
        try {
            await api.post(`/admin/badges/${badge.id}/quiz`, { question: q.trim(), options: cleanOpts, correct_index: correct, explanation: explanation.trim() });
            toast.success('Question added');
            setQ(''); setOpts(['', '', '', '']); setCorrect(0); setExplanation('');
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to add'); }
        finally { setSaving(false); }
    };

    const delQuestion = async (qid) => {
        try { await api.delete(`/admin/badges/${badge.id}/quiz/${qid}`); load(); }
        catch { toast.error('Delete failed'); }
    };

    return (
        <div className="fixed inset-0 z-[120] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="badge-quiz-manager">
            <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="sticky top-0 bg-white px-5 py-4 border-b border-slate-100 flex items-center gap-3 z-10">
                    <ListChecks className="w-5 h-5 text-amber-500" />
                    <div className="flex-1">
                        <div className="font-display font-bold text-slate-900">Quiz · {badge.name}</div>
                        <div className="text-xs text-slate-500">Learners pass this quiz (≥{badge.pass_percent}%) once they hit {badge.points_required} pts to earn the badge.</div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid="badge-quiz-manager-close"><X className="w-5 h-5 text-slate-500" /></button>
                </div>

                <div className="p-5 space-y-5">
                    {/* Existing questions */}
                    {loading ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : (
                        <div className="space-y-2">
                            {questions.length === 0 && <div className="text-sm text-slate-400">No questions yet. Add one below.</div>}
                            {questions.map((qq, i) => (
                                <div key={qq.id} className="bg-slate-50 rounded-xl p-3" data-testid={`bq-item-${i}`}>
                                    <div className="flex items-start gap-2">
                                        <div className="flex-1">
                                            <div className="font-semibold text-sm text-slate-800">Q{i + 1}. {qq.question}</div>
                                            <div className="mt-1 flex flex-wrap gap-1.5">
                                                {qq.options.map((o, oi) => (
                                                    <span key={oi} className={`text-xs px-2 py-0.5 rounded-full ${oi === qq.correct_index ? 'bg-emerald-100 text-emerald-700 font-bold' : 'bg-slate-200 text-slate-600'}`}>
                                                        {oi === qq.correct_index && <Check className="w-3 h-3 inline mr-0.5" />}{o}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                        <button onClick={() => delQuestion(qq.id)} className="p-1.5 rounded-lg hover:bg-rose-50" data-testid={`bq-del-${i}`}>
                                            <Trash2 className="w-4 h-4 text-rose-600" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Add new question */}
                    <div className="border-t border-slate-100 pt-4 space-y-2">
                        <div className="text-xs font-bold uppercase tracking-wider text-amber-600">Add a question</div>
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Question text" className={`${optInput} w-full`} data-testid="bq-question" />
                        {opts.map((o, i) => (
                            <div key={i} className="flex items-center gap-2">
                                <button type="button" onClick={() => setCorrect(i)} data-testid={`bq-correct-${i}`}
                                    className={`w-6 h-6 rounded-full grid place-items-center shrink-0 ${correct === i ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-400'}`}>
                                    {correct === i ? <Check className="w-3.5 h-3.5" /> : String.fromCharCode(65 + i)}
                                </button>
                                <input value={o} onChange={(e) => setOpts((arr) => arr.map((x, xi) => xi === i ? e.target.value : x))}
                                    placeholder={`Option ${String.fromCharCode(65 + i)}`} className={optInput} data-testid={`bq-opt-${i}`} />
                            </div>
                        ))}
                        <input value={explanation} onChange={(e) => setExplanation(e.target.value)} placeholder="Explanation (optional)" className={`${optInput} w-full`} data-testid="bq-explanation" />
                        <button onClick={addQuestion} disabled={saving} data-testid="bq-add"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-sm disabled:opacity-50">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add question
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
